import { useState, useMemo } from 'react';
import { Download, Plus, Minus, Lightbulb, Wrench, AlertTriangle, Filter, X } from 'lucide-react';
import {
  aggregateFahrzeugEvents,
  filterEvents,
  eventsToCSV,
  type FahrzeugForTimeline,
  type EventType,
  type TimelineFilter,
} from '@/lib/fahrzeuge-timeline';

const KOOP_RED = '#C41E3A';

interface EventConfig {
  label: string;
  icon: React.ReactNode;
  color: string;
  bg: string;
  dot: string;
}

const EVENT_CONFIG: Record<EventType, EventConfig> = {
  zugegangen: {
    label: 'Fahrzeug zugegangen',
    icon: <Plus className="w-3.5 h-3.5" />,
    color: '#16A34A',
    bg: '#DCFCE7',
    dot: '#22C55E',
  },
  verkauft: {
    label: 'Fahrzeug verkauft',
    icon: <Minus className="w-3.5 h-3.5" />,
    color: '#DC2626',
    bg: '#FEE2E2',
    dot: '#EF4444',
  },
  zurueckgegeben: {
    label: 'Fahrzeug zurückgegeben',
    icon: <Minus className="w-3.5 h-3.5" />,
    color: '#EA580C',
    bg: '#FFEDD5',
    dot: '#F97316',
  },
  empfehlung: {
    label: 'Verkaufs-Empfehlung',
    icon: <Lightbulb className="w-3.5 h-3.5" />,
    color: '#CA8A04',
    bg: '#FEF9C3',
    dot: '#EAB308',
  },
  werkstatt: {
    label: 'In Werkstatt',
    icon: <Wrench className="w-3.5 h-3.5" />,
    color: '#2563EB',
    bg: '#DBEAFE',
    dot: '#3B82F6',
  },
  hu_faellig: {
    label: 'HU fällig',
    icon: <AlertTriangle className="w-3.5 h-3.5" />,
    color: '#B45309',
    bg: '#FEF3C7',
    dot: '#F59E0B',
  },
};

const ALL_TYPES: EventType[] = [
  'zugegangen',
  'verkauft',
  'zurueckgegeben',
  'empfehlung',
  'werkstatt',
  'hu_faellig',
];

const BETRIEB_LABELS: Record<string, string> = {
  lcs_mietwagen: 'LCS Mietwagen',
  sv_buero: 'SV-Büro',
};

interface Props {
  fahrzeuge: FahrzeugForTimeline[];
}

export function FahrzeugeTimeline({ fahrzeuge }: Props) {
  const [betriebFilter, setBetriebFilter] = useState<string | null>(null);
  const [typenFilter, setTypenFilter] = useState<EventType[]>([]);
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const [showFilter, setShowFilter] = useState(false);

  const allEvents = useMemo(() => aggregateFahrzeugEvents(fahrzeuge), [fahrzeuge]);

  const filter: TimelineFilter = {
    betrieb: betriebFilter,
    typen: typenFilter,
    von: von ? new Date(von) : undefined,
    bis: bis ? new Date(bis + 'T23:59:59Z') : undefined,
  };

  const events = useMemo(() => filterEvents(allEvents, filter), [allEvents, filter]);

  const betriebe = useMemo(() => {
    const s = new Set(fahrzeuge.map((f) => f.betrieb).filter(Boolean));
    return Array.from(s);
  }, [fahrzeuge]);

  const toggleTyp = (t: EventType) => {
    setTypenFilter((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
    );
  };

  const clearFilters = () => {
    setBetriebFilter(null);
    setTypenFilter([]);
    setVon('');
    setBis('');
  };

  const hasActiveFilter =
    betriebFilter !== null || typenFilter.length > 0 || von !== '' || bis !== '';

  const downloadCSV = () => {
    const csv = eventsToCSV(events);
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fahrzeuge-timeline-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Group events by month for section headers
  const grouped = useMemo(() => {
    const map = new Map<string, typeof events>();
    for (const ev of events) {
      const key = ev.date.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ev);
    }
    return Array.from(map.entries());
  }, [events]);

  return (
    <div className="flex flex-col gap-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilter((s) => !s)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
              showFilter || hasActiveFilter
                ? 'border-red-300 bg-red-50 text-red-700'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}>
            <Filter className="w-3.5 h-3.5" />
            Filter
            {hasActiveFilter && (
              <span
                className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold text-white"
                style={{ backgroundColor: KOOP_RED }}>
                {[betriebFilter, ...typenFilter, von, bis].filter(Boolean).length}
              </span>
            )}
          </button>
          {hasActiveFilter && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-2 py-2 rounded-lg text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
              <X className="w-3 h-3" /> zurücksetzen
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{events.length} Ereignisse</span>
          <button
            onClick={downloadCSV}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all">
            <Download className="w-3.5 h-3.5" /> CSV herunterladen
          </button>
        </div>
      </div>

      {/* Filter panel */}
      {showFilter && (
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-4">
          {/* Betrieb */}
          {betriebe.length > 1 && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                Betrieb
              </p>
              <div className="flex flex-wrap gap-2">
                {betriebe.map((b) => (
                  <button
                    key={b}
                    onClick={() => setBetriebFilter(betriebFilter === b ? null : b)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      betriebFilter === b
                        ? 'border-red-300 bg-red-50 text-red-700'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-100'
                    }`}>
                    {BETRIEB_LABELS[b] ?? b}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Datumsbereich */}
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
              Datumsbereich
            </p>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={von}
                onChange={(e) => setVon(e.target.value)}
                className="px-3 py-2 rounded-lg border border-gray-200 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-red-200"
              />
              <span className="text-xs text-gray-400">bis</span>
              <input
                type="date"
                value={bis}
                onChange={(e) => setBis(e.target.value)}
                className="px-3 py-2 rounded-lg border border-gray-200 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-red-200"
              />
            </div>
          </div>

          {/* Ereignis-Typen */}
          <div>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
              Ereignis-Typ
            </p>
            <div className="flex flex-wrap gap-2">
              {ALL_TYPES.map((t) => {
                const cfg = EVENT_CONFIG[t];
                const active = typenFilter.includes(t);
                return (
                  <button
                    key={t}
                    onClick={() => toggleTyp(t)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all"
                    style={{
                      borderColor: active ? cfg.color : '#e5e7eb',
                      backgroundColor: active ? cfg.bg : 'white',
                      color: active ? cfg.color : '#6B7280',
                    }}>
                    {cfg.icon}
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Timeline */}
      {events.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <AlertTriangle className="w-10 h-10 text-gray-200 mb-3" />
          <p className="text-sm text-gray-400">Keine Ereignisse gefunden</p>
          {hasActiveFilter && (
            <button
              onClick={clearFilters}
              className="mt-2 text-xs text-red-500 hover:underline">
              Filter zurücksetzen
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-[19px] top-0 bottom-0 w-0.5 bg-gray-100" />

          <div className="space-y-0">
            {grouped.map(([month, monthEvents]) => (
              <div key={month}>
                {/* Month header */}
                <div className="flex items-center gap-3 mb-3 mt-5 first:mt-0">
                  <div className="w-10 h-10 flex-shrink-0" />
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                    {month}
                  </span>
                </div>

                {/* Events in this month */}
                {monthEvents.map((ev) => {
                  const cfg = EVENT_CONFIG[ev.type];
                  return (
                    <div key={ev.id} className="flex items-start gap-3 mb-3 group">
                      {/* Icon dot */}
                      <div
                        className="relative z-10 w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center border-2 border-white shadow-sm"
                        style={{ backgroundColor: cfg.bg, color: cfg.color }}>
                        {cfg.icon}
                      </div>

                      {/* Content card */}
                      <div className="flex-1 min-w-0 bg-white rounded-xl border border-gray-100 px-4 py-3 hover:shadow-sm transition-all"
                        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                                style={{ backgroundColor: cfg.bg, color: cfg.color }}>
                                {cfg.label}
                              </span>
                              {ev.kennzeichen && (
                                <span className="inline-block px-2 py-0.5 bg-gray-100 rounded font-mono text-[10px] font-bold text-gray-600">
                                  {ev.kennzeichen}
                                </span>
                              )}
                            </div>
                            <p className="text-sm font-semibold text-gray-900 mt-1 truncate">
                              {ev.fahrzeugLabel}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">{ev.beschreibung}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-[11px] text-gray-400">
                              {ev.date.toLocaleDateString('de-DE', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              })}
                            </p>
                            {ev.wert != null && (
                              <p className="text-sm font-bold mt-0.5" style={{ color: cfg.color }}>
                                {ev.wert.toLocaleString('de-DE', { minimumFractionDigits: 0 })} €
                              </p>
                            )}
                            {ev.betrieb && (
                              <p className="text-[10px] text-gray-300 mt-0.5">
                                {BETRIEB_LABELS[ev.betrieb] ?? ev.betrieb}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
